import { ApplicationConfig, importProvidersFrom, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { ChatReuseStrategy } from './shared/strategies/chat-reuse.strategy';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorInterceptor } from './shared/helpers/error.interceptor';
import { JwtInterceptor } from './shared/helpers/jwt.interceptor';
import { MsalInterceptor, MsalModule } from '@azure/msal-angular';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { ARTIFACT_RENDERERS } from './shared/models/artifact.model';
import { MarkdownArtifactComponent } from './shared/components/artifact-renderers/markdown-artifact.component';
import { DocumentArtifactComponent } from './shared/components/artifact-renderers/document-artifact.component';
import { DashboardArtifactComponent } from './shared/components/artifact-renderers/dashboard-artifact.component';

const isIE =
  typeof navigator !== 'undefined' &&
  (navigator.userAgent.indexOf('MSIE ') > -1 || navigator.userAgent.indexOf('Trident/') > -1);

function createMsalInstance(): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.MSAL_CONFIG.clientId,
      authority: environment.MSAL_CONFIG.authority,
      redirectUri: typeof window !== 'undefined' ? window.location.origin : environment.MSAL_CONFIG.redirectUri,
      postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : environment.MSAL_CONFIG.postLogoutRedirectUri,
    },
    cache: { cacheLocation: 'sessionStorage', storeAuthStateInCookie: isIE }
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: { preset: Aura, options: { darkModeSelector: '.app-dark' } }
    }),
    provideHttpClient(withInterceptorsFromDi()),
    // Registry of artifact-type -> panel renderer component. Adding a new report
    // type means adding one entry here; the chat and panel are untouched.
    {
      provide: ARTIFACT_RENDERERS,
      useValue: {
        markdown: MarkdownArtifactComponent,   // SF Account Summary, PCheck prep doc
        document: DocumentArtifactComponent,   // NEAT styled HTML report (iframe)
        html: DocumentArtifactComponent,
        pdf: DocumentArtifactComponent,
        dashboard: DashboardArtifactComponent, // Studio dashboard agents (native, interactive)
      },
    },
    { provide: RouteReuseStrategy, useClass: ChatReuseStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    importProvidersFrom(
      MsalModule.forRoot(
        createMsalInstance(),
        { interactionType: InteractionType.Redirect, authRequest: { scopes: ['user.read'] } },
        { interactionType: InteractionType.Redirect, protectedResourceMap: new Map([['https://graph.microsoft.com/v1.0/me', ['user.read']]]) }
      )
    )
  ]
};
